import { StyledEngineProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import enLocale from 'date-fns/locale/en-US';
import { BrowserRouter } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { EncodedQuery, QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';
import { parse, stringify } from 'qs';
import { PropsWithChildren } from 'react';
import {
  AppSettingsProvider,
  ModalProvider,
  ToastProvider,
  UserProvider,
  RedirectProvider,
  ThemeManager,
} from '.';
import { AuthProvider } from './AuthProvider/AuthProvider';
import { InsureIQApiClientProvider } from './InsureIQApiContextProvider';
import { LegacyIIQApiProvider } from './LegacyIIQApi/LegacyIIQApiContextProvider';
import { InsureIQQueryClientProvider } from './InsureIQQueryClientProvider';
import { HiqLogoutProvider } from '@harvestiq/ui/contexts/HiqLogoutProvider';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

export function AppProvider({ children }: PropsWithChildren) {
  const recaptchaKey = process.env.RECAPTCHA_KEY;

  if (!recaptchaKey) {
    console.error('RECAPTCHA_KEY is not defined in environment variables');
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaKey || ''}
      useEnterprise
      scriptProps={{ async: true, defer: true, appendTo: 'head' }}
    >
      <ToastProvider>
        <InsureIQQueryClientProvider>
          <BrowserRouter>
            <CompatRouter>
              <AppSettingsProvider>
                <RedirectProvider>
                  <AuthProvider>
                    <HiqLogoutProvider>
                      <LegacyIIQApiProvider>
                        <InsureIQApiClientProvider>
                          <StyledEngineProvider injectFirst>
                            <ThemeManager>
                              <QueryParamProvider
                                adapter={ReactRouter5Adapter}
                                options={{
                                  searchStringToObject: (queryString) =>
                                    parse(
                                      queryString.replace(/^\?/g, '')
                                    ) as EncodedQuery,
                                  objectToSearchString: stringify,
                                }}
                              >
                                <LocalizationProvider
                                  dateAdapter={AdapterDateFns}
                                  adapterLocale={enLocale}
                                >
                                  <UserProvider>
                                    <ModalProvider>{children}</ModalProvider>
                                  </UserProvider>
                                  <ReactQueryDevtools initialIsOpen={false} />
                                </LocalizationProvider>
                              </QueryParamProvider>
                            </ThemeManager>
                          </StyledEngineProvider>
                        </InsureIQApiClientProvider>
                      </LegacyIIQApiProvider>
                    </HiqLogoutProvider>
                  </AuthProvider>
                </RedirectProvider>
              </AppSettingsProvider>
            </CompatRouter>
          </BrowserRouter>
        </InsureIQQueryClientProvider>
      </ToastProvider>
    </GoogleReCaptchaProvider>
  );
}
